import type { ReactNode } from "react";

/**
 * Gráficos SVG da V2: sparkline, área e donut.
 *
 * São desenhos puros, sem biblioteca e sem estado: recebem números já
 * calculados por `analytics`. O SVG é marcado como decorativo e o significado
 * vai no `summary`, lido por leitores de tela — um gráfico nunca é a única
 * forma de acessar o número (AGENTS §17).
 */
export interface SeriesPoint {
  label: string;
  value: number;
}

function pathFrom(points: number[], width: number, height: number, pad: number): string {
  if (points.length === 0) return "";
  const max = Math.max(...points, 0);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const stepX = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0;

  return points
    .map((value, index) => {
      const x = pad + stepX * index;
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export interface SparklineProps {
  points: number[];
  /** Descrição textual do que a linha mostra. */
  summary: string;
  width?: number;
  height?: number;
  className?: string;
}

export function Sparkline({
  points,
  summary,
  width = 120,
  height = 32,
  className,
}: SparklineProps) {
  const pad = 2;
  return (
    <span className={className}>
      <svg
        className="cx-chart"
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <path className="cx-chart-line" d={pathFrom(points, width, height, pad)} />
      </svg>
      <span className="cx-sr-only">{summary}</span>
    </span>
  );
}

export interface AreaChartProps {
  series: SeriesPoint[];
  summary: string;
  height?: number;
  /** Linhas horizontais de grade. */
  gridLines?: number;
  className?: string;
}

export function AreaChart({
  series,
  summary,
  height = 180,
  gridLines = 4,
  className,
}: AreaChartProps) {
  const width = 640;
  const pad = 8;
  const values = series.map((point) => point.value);
  const line = pathFrom(values, width, height, pad);
  const area = line ? `${line} L${width - pad},${height - pad} L${pad},${height - pad} Z` : "";

  return (
    <div className={className}>
      <svg
        className="cx-chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        {Array.from({ length: gridLines }, (_, index) => {
          const y = pad + ((height - pad * 2) / (gridLines - 1 || 1)) * index;
          return <line key={index} className="cx-chart-grid" x1={0} x2={width} y1={y} y2={y} />;
        })}
        {area && <path className="cx-chart-area" d={area} />}
        {line && <path className="cx-chart-line" d={line} />}
      </svg>
      <span className="cx-sr-only">{summary}</span>
    </div>
  );
}

export interface DonutProps {
  /** Valor entre 0 e 1. */
  ratio: number;
  summary: string;
  size?: number;
  thickness?: number;
  /** Conteúdo central, normalmente o número já formatado. */
  children?: ReactNode;
  className?: string;
}

export function Donut({
  ratio,
  summary,
  size = 96,
  thickness = 10,
  children,
  className,
}: DonutProps) {
  const safe = Number.isFinite(ratio) ? Math.min(Math.max(ratio, 0), 1) : 0;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className={className} style={{ position: "relative", width: size, height: size }}>
      <svg
        className="cx-chart"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          className="cx-donut-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={thickness}
        />
        <circle
          className="cx-donut-value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={thickness}
          strokeDasharray={`${(circumference * safe).toFixed(2)} ${circumference.toFixed(2)}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {children && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          {children}
        </div>
      )}
      <span className="cx-sr-only">{summary}</span>
    </div>
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

export interface DonutSlicesProps {
  slices: readonly DonutSlice[];
  summary: string;
  size?: number;
  thickness?: number;
  /** Conteúdo central, normalmente o total já formatado. */
  children?: ReactNode;
  className?: string;
}

/**
 * Donut de várias fatias.
 *
 * Cada fatia recebe a própria cor porque o valor está na legenda ao lado: o
 * anel ordena as grandezas, e quem lê o número lê a legenda (AGENTS §11 — cor
 * não é o único sinal).
 */
export function DonutSlices({
  slices,
  summary,
  size = 126,
  thickness = 18,
  children,
  className,
}: DonutSlicesProps) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = slices.reduce((sum, slice) => sum + slice.value, 0) || 1;
  let offset = 0;

  return (
    <div className={className} style={{ position: "relative", width: size, height: size }}>
      <svg
        className="cx-chart"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <circle className="cx-donut-track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={thickness} />
        {slices.map((slice) => {
          const length = (slice.value / total) * circumference;
          const node = (
            <circle
              key={slice.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={thickness}
              strokeDasharray={`${length.toFixed(2)} ${(circumference - length).toFixed(2)}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += length;
          return node;
        })}
      </svg>
      {children && <div className="cx-donut-center">{children}</div>}
      <span className="cx-sr-only">{summary}</span>
    </div>
  );
}
